// api/aws/organizations_table/useRequestApproval.ts
import { SEARCH_DATA_KEY } from 'api/search'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNotifyApprovers } from 'api/slack/useNotifyApprovers'
import { requestApprovalUpdates } from 'context/DealsDetailsProvider/dealUpdateData'
import { Approver, Requestor } from 'types'
import { useBatchRequestApproval } from '../organizations_table/useBatchRequestApproval'

type Args = {
  dealPK: string
  dealSK: string
  deal_uuid: string
  approvers: Approver[]
  requestedBy: string
  requestor: Requestor
  // optional: if you already build the Slack payload outside
  notificationPayload?: any
}

export function useRequestApproval() {
  const qc = useQueryClient()
  // const batchUpdate = useBatchUpdateItems()
  const batchUpdate = useBatchRequestApproval()
  const notify = useNotifyApprovers() // your existing Slack notifier

  const mutation = useMutation({
    // one function to do both: save -> notify
    mutationFn: async (args: Args) => {
      const { dealPK, dealSK, deal_uuid: _deal_uuid, approvers, requestedBy, requestor, notificationPayload } = args

      // 1) Optimistic cache patch
      const prevAll = qc.getQueryData<any>(SEARCH_DATA_KEY)
      qc.setQueryData(SEARCH_DATA_KEY, (data: any) => {
        if (!data) return data
        const next = { ...data }
        next.deals = (data.deals ?? []).map((d: any) =>
          d.PK === dealPK && d.SK === dealSK
            ? {
                ...d,
                isRequested: true,
                status: 'Approval Requested',
                stage: 'Pending Approval',
                requestedBy,
                requestedTime: new Date().toISOString(),
                approvers,
                requestor: requestor ? { ...requestor, hasRequested: true } : undefined,
              }
            : d
        )
        return next
      })

      try {
        // 2) Persist the deal field changes
        const updates = requestApprovalUpdates('ORGANIZATIONS_TABLE', dealPK, dealSK, requestor, requestedBy, approvers)
        await batchUpdate.mutateAsync(updates)

        // 3) Notify approvers (Slack)
        if (notificationPayload) {
          await notify.mutateAsync(notificationPayload)
        }
      } catch (e) {
        // rollback optimistic patch
        if (prevAll) qc.setQueryData(SEARCH_DATA_KEY, prevAll)
        throw e
      } finally {
        // ensure fresh server state (but UI already feels instant)
        qc.invalidateQueries({ queryKey: SEARCH_DATA_KEY })

        // Invalidate org-level cache used by deal details pages
        qc.invalidateQueries({ queryKey: ['all_org_items', dealPK] })

        // Update item-level cache for the specific deal
        qc.setQueryData(['item', dealPK, dealSK], (old: any) => {
          if (!old) return old
          return {
            ...old,
            isRequested: true,
            status: 'Approval Requested',
            stage: 'Pending Approval',
            requestedBy,
            requestedTime: new Date().toISOString(),
            approvers,
            requestor: requestor ? { ...requestor, hasRequested: true } : undefined,
          }
        })
      }

      return { ok: true }
    },
  })

  return {
    requestApproval: mutation.mutateAsync,
    isRequesting: mutation.isPending,
  }
}

// // api/aws/organizations_table/useRequestApproval.ts
// import { useMutation, useQueryClient } from '@tanstack/react-query'
// import { Approver, Requestor } from 'types'

// type Args = {
//   dealPK: string
//   dealSK: string
//   deal_uuid: string
//   approvers: Approver[]
//   requestedBy: string
//   requestor: Requestor
// }

// async function postRequestApproval(body: Args) {
//   const res = await fetch('/org/request-approval', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({
//       PK: body.dealPK,
//       SK: body.dealSK,
//       deal_uuid: body.deal_uuid,
//       approvers: body.approvers,
//       requestedBy: body.requestedBy,
//       requestor: body.requestor,
//     }),
//   })
//   if (!res.ok) throw new Error('Failed to request approval')
//   return res.json()
// }

// export function useRequestApproval() {
//   const qc = useQueryClient()

//   const mutation = useMutation({
//     mutationFn: postRequestApproval,
//     // optimistic cache patch
//     onMutate: async args => {
//       await qc.cancelQueries({ queryKey: SEARCH_DATA_KEY })
//       const prev = qc.getQueryData<any>(SEARCH_DATA_KEY)

//       qc.setQueryData(SEARCH_DATA_KEY, (data: any) => {
//         if (!data) return data
//         const next = { ...data }
//         next.deals = (data.deals ?? []).map((d: any) =>
//           d.PK === args.dealPK && d.SK === args.dealSK
//             ? {
//                 ...d,
//                 isRequested: true,
//                 status: 'Approval Requested',
//                 requestedBy: args.requestedBy,
//                 requestedTime: new Date().toISOString(),
//                 approvers: args.approvers,
//                 requestor: args.requestor ? { ...args.requestor, hasRequested: true } : undefined,
//               }
//             : d
//         )
//         return next
//       })

//       return { prev }
//     },
//     onError: (_err, _args, ctx) => {
//       if (ctx?.prev) qc.setQueryData(SEARCH_DATA_KEY, ctx.prev)
//     },
//     onSettled: () => {
//       // pull fresh server state but UI is already snappy
//       qc.invalidateQueries({ queryKey: SEARCH_DATA_KEY })
//     },
//   })

//   return {
//     requestApproval: mutation.mutateAsync,
//     isRequesting: mutation.isPending,
//   }
// }
